import { IModalProps, ISelectOption, Select, SelectSummary } from '@aon/aon-tws-common-ui';
import React, { useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../appStore';
import ContentConstants from '../../contentConstants';
import { DisplayLogicConditionError, SurveyStep } from '../../models';
import { ActionTypes as CommonActionTypes } from '../../store/ActionTypes';
import {
	displayLogicQuestionToSelectOption,
	getErrorCondition,
	resetQuestionBranchingLogic,
	saveGroupQuestionBranchingLogic,
	selectDisplayLogicQuestions,
	updateQuestion,
} from '../../store/QuestionBranchingLogicSlice';
import { ActionCreators as SurveyBuilderActionCreators } from '../../store/SurveyBuilderStore';
import { ActionCreators as SurveyActionCreators } from '../../store/SurveyStore';
import { assertNonNullable } from '../../utilities/assertions';
import { useModalState } from '../../utilities/hooks';
import { LoadingIndicator, WarningModal } from '../common';
import { DisplayLogicConditionErrorMessage } from '../displayLogic/DisplayLogicConditionErrorMessage';
import { ColumnLayoutModal } from './ColumnLayoutModal';
import { QuestionBranchingLogic } from './QuestionBranchingLogic';

type GroupQuestionBranchingLogicModalProps = Pick<IModalProps, 'isOpen' | 'onClosed'>;

const GroupQuestionBranchingLogicModal = ({ isOpen, onClosed }: GroupQuestionBranchingLogicModalProps) => {
	const dispatch = useAppDispatch();

	const {
		surveyEditModel: {
			survey: { surveyId },
			audienceSource: { audienceSourceType },
		},
	} = useAppSelector((state) => state.survey);

	const [questionSelectionState, setQuestionSelectionState] = useState<{
		ids: number[];
		firstDisplayedOnSurvey?: { id: number; displayOrder: number };
	}>({
		ids: [],
	});

	const { isOpen: isWarningModalOpen, toggleModal: toggleWarningModal } = useModalState();

	const { isLoadingOrSaving, unsavedBranchingLogicChanges, branchingLogicEditModels } = useAppSelector(
		(state) => state.questionBranchingLogic,
	);

	const maxDisplayOrder = useAppSelector((state) => state.survey.surveyEditModel.questions.length);
	const displayLogicQuestions = useAppSelector((state) => selectDisplayLogicQuestions(state, { maxDisplayOrder }));

	const questionOptions = displayLogicQuestions.map(displayLogicQuestionToSelectOption);

	const handleQuestionSelectionChange = (values?: string | string[]) => {
		if (!values || typeof values === 'string') {
			return;
		}

		updateQuestionSelectionState(values.map((v) => +v));
	};

	const handleQuestionDismissed = (values: ISelectOption[]) => updateQuestionSelectionState(values.map((v) => +v.name));

	const updateQuestionSelectionState = (selectedIds: number[]) => {
		if (!selectedIds.length) {
			reset();
			return;
		}

		const { surveyQuestionId: id, displayOrder } = displayLogicQuestions
			.filter((q) => !!q.surveyQuestionId && selectedIds.includes(q.surveyQuestionId))
			.reduce((pq, cq) => (pq.displayOrder < cq.displayOrder ? pq : cq));

		assertNonNullable(id);
		assertNonNullable(displayOrder);

		dispatch(
			updateQuestion({
				questionId: id,
				questionDisplayOrder: displayOrder,
				oldQuestionDisplayOrder: questionSelectionState.firstDisplayedOnSurvey?.displayOrder ?? 0,
			}),
		);
		setQuestionSelectionState({
			ids: selectedIds,
			firstDisplayedOnSurvey: {
				id,
				displayOrder,
			},
		});
	};

	const reset = () => {
		setQuestionSelectionState({ ids: [] });
		dispatch(resetQuestionBranchingLogic());
	};

	const handleClosed = (sourceIsWarningModal?: boolean) => {
		if ((unsavedBranchingLogicChanges && !sourceIsWarningModal) || sourceIsWarningModal) {
			toggleWarningModal();
		}

		if (sourceIsWarningModal || (!sourceIsWarningModal && !unsavedBranchingLogicChanges)) {
			reset();
			onClosed();
		}
	};

	const handleSubmit = () =>
		dispatch(saveGroupQuestionBranchingLogic({ surveyId, questionIds: questionSelectionState.ids }))
			.unwrap()
			.then(() => {
				dispatch({ type: CommonActionTypes.TOAST_SUCCESS_GENERIC_NOTIFICATION });
				questionSelectionState.ids.forEach((questionId) =>
					dispatch(SurveyActionCreators.UpdateSurveyQuestionDisplayRulesCount(questionId, branchingLogicEditModels.length, 'add')),
				);
				reset();
				onClosed();
			})
			.catch(() => {
				dispatch({
					type: CommonActionTypes.TOAST_WARNING_CUSTOM_NOTIFICATION,
					payload: {
						message: ContentConstants.TOAST_ERROR_GENERIC_BODY,
						messageTitle: ContentConstants.TOAST_ERROR_GENERIC_TITLE,
					},
				});
			});

	const jumpToStep = (destinationStep: SurveyStep) => {
		reset();
		onClosed();
		dispatch(SurveyBuilderActionCreators.Jump(destinationStep));
	};

	return (
		<ColumnLayoutModal
			contentfullwidth={true}
			contenthcentered={false}
			contentoverflow={true}
			isOpen={isOpen}
			onClosed={handleClosed}
			onSubmit={handleSubmit}
			primaryButtonText={ContentConstants.BUTTON_SAVE_CHANGES}
			secondaryButtonText={ContentConstants.BUTTONS_CANCEL}
			title={ContentConstants.LOGIC_AND_LAYOUT.GROUP_QUESTION_LOGIC.TITLE}
		>
			{isLoadingOrSaving ? (
				<LoadingIndicator />
			) : (
				<>
					<div className='row form-group'>
						<div className='col'>
							<DisplayLogicConditionErrorMessage
								ResetQuestionDisplayLogic={reset}
								Jump={jumpToStep}
								errorCondition={getErrorCondition(audienceSourceType)}
							/>
						</div>
					</div>
					<div className='row form-group'>
						<div className='col col-md-5'>
							<p id='description-select-questions'>{ContentConstants.LOGIC_AND_LAYOUT.GROUP_QUESTION_LOGIC.QUESTIONS.DESCRIPTION}</p>
							<Select
								disabled={getErrorCondition(audienceSourceType) === DisplayLogicConditionError.NoSourceSelected}
								displayText={ContentConstants.LOGIC_AND_LAYOUT.GROUP_QUESTION_LOGIC.QUESTIONS.LABEL}
								aria-describedby='description-select-questions'
								items={questionOptions}
								onApply={handleQuestionSelectionChange}
								selectedValues={questionSelectionState.ids.map((id) => id.toString())}
								multiple={true}
								showCount={true}
								size='md'
								selectSize='xlg'
								dropdownSize='sm'
								showSearch={true}
								placeHolderText={ContentConstants.LOGIC_AND_LAYOUT.GROUP_QUESTION_LOGIC.QUESTIONS.SEARCH_PLACEHOLDER}
							/>
						</div>
					</div>
					<div className='row form-group'>
						<div className='col'>
							<SelectSummary
								enableDismiss
								selected={questionOptions.filter((o) => questionSelectionState.ids.includes(+o.name))}
								updateSelect={handleQuestionDismissed}
							/>
						</div>
					</div>
					<div className='row'>
						<div className='col'>
							<QuestionBranchingLogic questionId={questionSelectionState.firstDisplayedOnSurvey?.id} scope='group' />
						</div>
					</div>
				</>
			)}
			<WarningModal
				contentBody={ContentConstants.WARNING_MODAL_UNSAVED_BODY}
				contentHeadline={ContentConstants.WARNING_MODAL_UNSAVED_HEADLINE}
				isOpen={isWarningModalOpen}
				onClose={toggleWarningModal}
				onSubmit={() => handleClosed(true)}
			/>
		</ColumnLayoutModal>
	);
};

export { GroupQuestionBranchingLogicModal, type GroupQuestionBranchingLogicModalProps };
